import { useCallback, type ReactNode, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  Count,
  type DisconnectImpact,
  DisconnectWarning,
} from "../components/DisconnectWarning";
import { useGroupRemovalDisconnectImpact } from "./useGroupRemovalDisconnectImpact";

// Guards a group-membership removal behind a confirmation when the removed
// member would lose connector access that workflows or git sync in their
// spaces rely on. Removals with no visible impact proceed silently.
export function useGroupRemovalDisconnectGuard({
  userGroupIds,
  ready,
}: {
  userGroupIds: readonly string[];
  ready: boolean;
}): {
  guard: (
    groupId: string,
    removedUserIds: readonly string[],
    proceed: () => void
  ) => void;
  modal: ReactNode;
  loaded: boolean;
} {
  const [computeImpact, computeImpactLoaded] =
    useGroupRemovalDisconnectImpact(userGroupIds);
  const [pending, setPending] = useState<{
    proceed: () => void;
    impact: DisconnectImpact;
  } | null>(null);

  const loaded = ready && computeImpactLoaded;
  const guard = useCallback(
    (
      groupId: string,
      removedUserIds: readonly string[],
      proceed: () => void
    ) => {
      if (!ready || (removedUserIds.length > 0 && !computeImpactLoaded)) return;
      const impact = computeImpact(groupId, removedUserIds);
      if (impact.workflows.length === 0 && impact.gitSpaceNames.length === 0) {
        proceed();
        return;
      }
      setPending({ proceed, impact });
    },
    [computeImpact, ready, computeImpactLoaded]
  );

  const impact = pending?.impact;
  const workflowCount = impact?.workflows.length ?? 0;
  const hasWorkflows = workflowCount > 0;
  const modal = (
    <ConfirmModal
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        if (!loaded) return;
        pending?.proceed();
        setPending(null);
      }}
      title="Remove from group?"
      description={
        hasWorkflows ? (
          <>
            Removing the member means{" "}
            <Count n={workflowCount} noun="workflow" /> will need a connector
            replaced.
          </>
        ) : (
          "This group provides connector access git sync may rely on. Removing the member removes that access."
        )
      }
      confirmLabel="Remove"
      confirmDisabled={!loaded}
      destructive
    >
      {impact && <DisconnectWarning {...impact} />}
    </ConfirmModal>
  );

  return { guard, modal, loaded };
}
